import { Component, Input, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { TransactionReportingService } from 'app/core/services/transaction-reporting.service';
import { Subject, Subscription } from 'rxjs';
import { Items } from 'app/core/models/items';
import { WalletByTransaction } from 'app/core/models/walletByTransaction';
import { NotifMessageService } from 'app/core/services/notifmessage.service';
import { isNullOrUndefined } from 'util';
import { MatTableDataSource } from '@angular/material/table';

@Component({
  selector: 'app-wallet-reports',
  templateUrl: './wallet-reports.component.html',
  styleUrls: ['./wallet-reports.component.css']
})
export class WalletReportsComponent implements OnInit {

  walletForm: FormGroup;

  walletReportTypeItems: Array<String> = Array<String>("TOP_WALLET_BY_AMOUNT", "TOP_WALLET_BY_TRANSACTION");
  currencyItems: Array<string> = Array<string>("EUR", "USD");
  numberOfItemsList: Array<number> = Array<number>(3, 5, 10, 20);
  periodItems: Array<number> = Array<number>(1, 3, 6, 12);

  period: number = 3;
  walletReportType: string = "TOP_WALLET_BY_TRANSACTION";
  currency: string = "EUR";
  numberOfItems: number = 10;
  chartType: String = "bar";

  walletReportTypeSub: Subscription;
  walletReportPeriodSub: Subscription;
  numberOfItemsSubscription: Subscription;
  currencySubscription: Subscription;
  chartTypeSubscription: Subscription;
  subscription: Subscription;

  transactionsReport: Items<WalletByTransaction>;
  walletIdList: string[];

  fromDate: Date;
  isTransactionReport: boolean;
  isLoading: boolean;

  reportData: number[];
  reportLabel: string[];
  reportTitle: string;

  displayedColumns = ['walletID', 'transactionCount'];


  criteria = {
    count: this.numberOfItems,
    windowBegin: this.getFromDate(this.period),
    windowEnd: new Date()
  };

  constructor(
    private formBuilder: FormBuilder,
    private translate: TranslateService,
    private tsxReportingService: TransactionReportingService,
    private notifService: NotifMessageService) { }

  ngOnInit() {

    this.walletForm = this.formBuilder.group({
      walletReportType: new FormControl(this.walletReportType, Validators.required),
      period: new FormControl(this.period, Validators.required),
      numberOfItems: new FormControl(this.numberOfItems, Validators.required),
      currency: new FormControl(this.currency),
      chartType: new FormControl('bar')
    });

    this.getTopWalletByTransaction();

    this.numberOfItemsSubscription = this.walletForm.get('numberOfItems').valueChanges.subscribe(val => {
      this.numberOfItems = val;
      if (this.isFormValid()) {
        this.criteria.count = this.numberOfItems;
        if (this.walletReportType == "TOP_WALLET_BY_TRANSACTION") {
          this.getTopWalletByTransaction();
        } else if (this.walletReportType == "TOP_WALLET_BY_AMOUNT") {
          this.getTopWalletByAmount();
        }
      }
    });

    this.currencySubscription = this.walletForm.get('currency').valueChanges.subscribe(val => {
      this.currency = val;
      if (this.isFormValid()) {
        this.criteria["currency"] = this.currency;
        if (this.walletReportType == "TOP_WALLET_BY_TRANSACTION") {
          this.getTopWalletByTransaction();
        } else if (this.walletReportType == "TOP_WALLET_BY_AMOUNT") {
          this.getTopWalletByAmount();
        }
      }
    });

    this.walletReportTypeSub = this.walletForm.get("walletReportType").valueChanges.subscribe(val => {
      this.walletReportType = val;
      if (this.isFormValid()) {
        if (!isNullOrUndefined(this.period)) {
          this.criteria.windowEnd = new Date();
          this.criteria.windowBegin = this.getFromDate(this.period);
        }
        if (val == "TOP_WALLET_BY_TRANSACTION") {
          this.getTopWalletByTransaction();
        } else if (val == "TOP_WALLET_BY_AMOUNT") {
          this.getTopWalletByAmount();
        }
      }
    });

    this.walletReportPeriodSub = this.walletForm.get("period").valueChanges.subscribe(val => {
      this.period = val;
      if (this.isFormValid()) {
        this.criteria.windowEnd = new Date();
        this.criteria.windowBegin = this.getFromDate(val);
        if (isNullOrUndefined(this.walletReportType)) {
          this.getTopWalletByTransaction();
        }
        if (this.walletReportType == "TOP_WALLET_BY_TRANSACTION") {
          this.getTopWalletByTransaction();
        } else if (this.walletReportType == "TOP_WALLET_BY_AMOUNT") {
          this.getTopWalletByAmount();
        }
      }
    });

    this.chartTypeSubscription = this.walletForm.get("chartType").valueChanges.subscribe(val => {
      this.chartType = val;
    });

  }

  isFormValid(): boolean {
    if (this.walletForm.get("walletReportType").valid && this.walletForm.get("period").valid && this.walletForm.get("numberOfItems").valid
      && (this.isTransactionReport || this.walletForm.get("currency").valid))
      return true;
    return false;
  }

  getFromDate(period: number): Date {
    this.fromDate = new Date();
    this.fromDate.setMonth(this.fromDate.getMonth() - period);
    return this.fromDate;
  }

  getTopWalletByTransaction(): void {
    this.isTransactionReport = true;
    this.isLoading = true;

    this.tsxReportingService.getNumberOfPaymentMeansByPeriod(this.criteria, "/topWallets/byTxCount").subscribe(
      items => {
        this.transactionsReport = items;
        this.reportLabel = items.map(items => items.walletId);
        this.reportData = items.map(items => items.transactionCount);
        this.reportTitle = this.translate.instant("TOP_WALLET_BY_TRANSACTION");
        this.isLoading = false;
        if (items.empty) {
          this.notifService.notifyInfo('EMPTY_RESULT');
        }
      });
  }

  getTopWalletByAmount(): void {
    this.isTransactionReport = false;
    this.isLoading = true;
    this.criteria["currency"] = this.currency;
    this.reportTitle = this.translate.instant("TOP_WALLET_BY_AMOUNT");
    this.tsxReportingService.getNumberOfPaymentMeansByPeriod(this.criteria, "/topWallets/byAmount").subscribe(
      items => {
        this.transactionsReport = items;
        this.reportLabel = items.map(items => items.walletId);
        this.reportData = items.map(items => items.totalAmount / 100);
        this.isLoading = false;
        if (items.empty) {
          this.notifService.notifyInfo('EMPTY_RESULT');
        }
      });
  }

  date() {
    let frDateFormat = "dd MMM y";
    let usDateFormat = "MMM dd y";
    if (this.translate.currentLang == "fr_FR")
      return frDateFormat;
    else
      return usDateFormat;
  }

  locale() {
    if (this.translate.currentLang == "fr_FR")
      return "fr_FR";
    else
      return "en_US";
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.walletReportTypeSub) {
      this.walletReportTypeSub.unsubscribe();
    }
    if (this.currencySubscription) {
      this.currencySubscription.unsubscribe();
    }
    if (this.numberOfItemsSubscription) {
      this.numberOfItemsSubscription.unsubscribe();
    }
    if (this.chartTypeSubscription) {
      this.chartTypeSubscription.unsubscribe();
    }
  }
}
